/**
 * CompactAgenda Component
 * 
 * Compact version of FullAgenda with reduced spacing, smaller fonts, and floating bookmark bar.
 * Displays all events in a condensed format for better overview.
 */

import React, { useState, useEffect, useRef } from 'react';
import '../App.css';

// Centralized imports
import { useAuth, AuthGuard } from '../utils/auth';
import { useScrollWobble } from '../utils/scrollAnimations';
import { PAGE_EVENTS, VENUE_INFO } from '../data/eventsData';
import EventCard from '../components/EventCard';
import ClosingSection from '../components/shared/ClosingSection';

function CompactAgenda() {
  // Use centralized authentication hook
  const { isValidAccess, isLoading } = useAuth('/cmpctgnd5628vqr');
  
  // Use centralized scroll animation hook
  useScrollWobble();
  
  // State for bookmark bar visibility
  const [isBookmarkVisible, setIsBookmarkVisible] = useState(false);
  const [isBookmarkHidden, setIsBookmarkHidden] = useState(false);
  const [activeEvent, setActiveEvent] = useState('');
  const scrollTimeoutRef = useRef(null);
  
  // Function to set/reset the hide timeout
  const setHideTimeout = () => {
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set new timeout to hide bookmark bar after 1.5 seconds of inactivity
    scrollTimeoutRef.current = setTimeout(() => {
      setIsBookmarkHidden(true);
    }, 1500);
  };
  
  // Handle scroll to show/hide bookmark bar
  useEffect(() => {
    const handleScroll = () => {
      const venueSection = document.querySelector('.location-section');
      if (venueSection) {
        const venueSectionBottom = venueSection.offsetTop + venueSection.offsetHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Show bookmark bar after venue section
        const shouldShowBookmark = scrollTop > venueSectionBottom - 100;
        setIsBookmarkVisible(shouldShowBookmark);
        
        // Show bookmark bar on any scroll movement and set timeout
        if (shouldShowBookmark) {
          setIsBookmarkHidden(false);
          setHideTimeout();
        }
        
        // Determine active event based on scroll position
        const eventElements = document.querySelectorAll('[data-event-id]');
        let currentActiveEvent = '';
        
        eventElements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            currentActiveEvent = element.getAttribute('data-event-id');
          }
        });
        
        setActiveEvent(currentActiveEvent);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
  
  // Set initial timeout when bookmark becomes visible
  useEffect(() => {
    if (isBookmarkVisible && !isBookmarkHidden) {
      setHideTimeout();
    }
  }, [isBookmarkVisible, isBookmarkHidden]);
  
  // Function to scroll to specific event
  const scrollToEvent = (eventId) => {
    const element = document.querySelector(`[data-event-id="${eventId}"]`);
    if (element) {
      // Keep the bookmark bar visible during navigation
      setIsBookmarkHidden(false);
      
      // Clear any existing timeout to prevent immediate hiding
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Set a longer timeout after user interaction (5 seconds instead of 1.5)
      scrollTimeoutRef.current = setTimeout(() => {
        setIsBookmarkHidden(true);
      }, 5000);
    }
  };

  // Get all events from centralized data
  const events = PAGE_EVENTS.home();
  
  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});
  
  // Sort dates to ensure proper order
  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    // Parse dates for proper sorting
    const dateA = new Date(a.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    const dateB = new Date(b.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return dateA - dateB;
  });
  
  // Function to get appropriate header and ornament for each date
  const getDateHeaderInfo = (date) => {
    if (date === 'Saturday, 22nd November 2025') {
      return { title: 'Pre-Wedding Ceremonies', ornament: '🌷' };
    } else if (date === 'Monday, 24th November 2025') {
      return { title: 'Pre-Wedding Celebrations', ornament: '✨' };
    } else if (date === 'Tuesday, 25th Nov 2025') {
      return { title: 'Wedding Day & Reception', ornament: '💕' };
    } else {
      return { title: 'Wedding Events', ornament: '🌸' };
    }
  };

  return (
    <AuthGuard isLoading={isLoading} isValidAccess={isValidAccess}>
      <div className="app compact-agenda">
        {/* Hero Section */}
        <div className="hero">
          <div className="hero-text">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>22nd, 24th & 25th Nov 2025</p>
          </div>
        </div>

        {/* Hero Text Section for Mobile */}
        <div className="hero-text-section">
          <div className="hero-text-content">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>22nd, 24th & 25th Nov 2025</p>
          </div>
        </div>

        <div className="gradient-container">
          {/* Location Section */}
          <div className="location-section compact-location-section">
            <div className="location-container">
              <div className="location-title">
                <img src="/location.png" alt="Location" className="location-icon" />
                <h2>Wedding Venue</h2>
              </div>
              <div className="location-details">
                <h3>{VENUE_INFO.name}</h3>
                <div className="location-address">
                  {VENUE_INFO.address.map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Events Section */}
          <div className="agenda">
            {/* Events grouped by date */}
            {sortedDates.map((date, dateIndex) => {
              const headerInfo = getDateHeaderInfo(date);
              return (
                <div key={date}>
                  {/* Date Header */}
                  <div className="day-section compact-day-section">
                    <div className="day-header">
                      <h2>{headerInfo.title}</h2>
                      <div className="date-ornament">
                        <span className="ornament">{headerInfo.ornament}</span>
                        <span className="date">{date}</span>
                        <span className="ornament">{headerInfo.ornament}</span>
                      </div>
                    </div>
                  </div>

                  {/* Event Cards for this date */}
                  {eventsByDate[date].map((event, eventIndex) => (
                    <div key={event.id} data-event-id={event.id} className="compact-event">
                      <EventCard 
                        event={event} 
                        isReversed={eventIndex % 2 === 1}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Floating Bookmark Bar */}
          {isBookmarkVisible && (
            <div 
              className={`bookmark-bar ${isBookmarkHidden ? 'hidden' : ''}`}
              onMouseEnter={() => {
                // Keep bookmark visible when hovering
                setIsBookmarkHidden(false);
                if (scrollTimeoutRef.current) {
                  clearTimeout(scrollTimeoutRef.current);
                }
              }}
              onMouseLeave={() => {
                // Set timeout to hide when mouse leaves
                setHideTimeout();
              }}
            >
              <div className="bookmark-header">
                <h3>Events</h3>
                <span className="bookmark-ornament">✨</span>
              </div>
              <div className="bookmark-items">
                {events.map((event) => (
                  <button
                    key={event.id}
                    className={`bookmark-item ${activeEvent === event.id ? 'active' : ''}`}
                    onClick={() => scrollToEvent(event.id)}
                    title={`${event.title} - ${event.time}`}
                  >
                    <span className="bookmark-title">{event.title}</span>
                    <span className="bookmark-time">{event.time}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Closing Section */}
          <ClosingSection 
            variant="simple"
            className="compact-closing-section"
          />
        </div>
      </div>
    </AuthGuard>
  );
}

export default CompactAgenda;